<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import ContactCTA from '@/Pages/Home/Partials/ContactCTA.vue'
import { about } from '@/actions/App/Http/Controllers/PageController'
import { DocumentIcon } from '@heroicons/vue/24/outline'
import { usePage } from '@inertiajs/vue3'
import { ref } from 'vue'

const timeline = [
    {
        name: 'Jsme certifikovaní',
        description: 'Pravidelně se školíme k aplikaci foukané izolace Supafil od Knauf Insulation a Pure Floc od Ursa',
        certificates: [
            {
                name: 'Horčičák - Enroll',
                link: '/src/certificates/horcicak_enroll.jpg',
            },
            {
                name: 'Horčičák - Knauf',
                link: '/src/certificates/horcicak_knauf.pdf',
            },
            {
                name: 'Horčičák - Ursa',
                link: '/src/certificates/horcicak_ursa.jpg',
            },
            {
                name: 'Horčičák - Rigips',
                link: '/src/certificates/horcicak_rigips.pdf',
            },
            {
                name: 'Horčičák - Hilti',
                link: '/src/certificates/horcicak_hilti.pdf',
            },
            {
                name: 'Horčičák - KCS',
                link: '/src/certificates/horcicak_kcs.pdf',
            },
            {
                name: 'Steiger - Enroll',
                link: '/src/certificates/steiger_enroll.jpg',
            },
            {
                name: 'Steiger - Knauf',
                link: '/src/certificates/steiger_knauf.pdf',
            },
        ],
    },
    {
        name: 'Pravidelně se školíme',
        description: 'Naše firma se pravidelně školí a vzdělává, abychom byli vždy o krok napřed',
    },
    {
        name: 'Zateplíme Váš dům',
        description: 'Dům vám zateplíme certifikovaným materiálem a bez tepelných mostů',
    },
]

const defaultSelectedService = {
    id: 1,
    name: 'Kvalitně, čistě, rychle',
    description: 'Práci odvedeme kvalitně, rychle a vše si uklidíme',
    img: {
        src: '/src/abouts/kvalitne_rychle_ciste.webp',
        alt: 'Kvalitní a rychlá práce - sádrokartony a izolace',
    },
}

const services = [
    {
        id: 1,
        name: 'Kvalitně, čistě, rychle',
        description: 'Práci odvedeme kvalitně, rychle a vše si uklidíme',
        img: {
            src: '/src/abouts/kvalitne_rychle_ciste.webp',
            alt: 'Kvalitní práce sádrokartonů a izolace',
        },
    },
    {
        id: 2,
        name: 'Do jednoho dne',
        description: 'Zateplování domu o průměrné výměře nebude trvat déle než jeden den',
        img: {
            src: '/src/abouts/do_jednoho_dne.webp',
            alt: 'Rychlé zateplení domu do jednoho dne',
        },
    },
    {
        id: 3,
        name: 'Vaše zateplení provedeme v krátkém termínu',
        description: 'Zateplování domu o průměrné výměře nebude trvat déle než jeden den',
        img: {
            src: '/src/abouts/vase_zatepleni_provedeme_rychle.webp',
            alt: 'Rychlé zateplení v krátkém termínu',
        },
    },
]

const hoveredService = ref(defaultSelectedService)
</script>

<template>
    <AppLayout
        meta_title="Řemeslníci na sádrokarton, foukané izolace a betonové stěrky"
        meta_description="Jsme zkušení řemeslníci z Olomouce, specializovaní na sádrokarton, foukanou izolaci a betonové stěrky. Působíme v celém Olomouckém kraji."
        :meta_canonical="about.url()"
    >
        <template #content>
            <section class="section-width mt-14">
                <div class="space-y-20">
                    <div
                        class="mx-auto place-content-center lg:mx-0 lg:grid lg:max-w-none lg:grid-cols-2 lg:gap-8 xl:gap-x-16"
                    >
                        <div class="flex flex-col justify-center space-y-8 lg:space-y-6">
                            <header class="header-width">
                                <h1 class="header-h">Kdo jsme?</h1>

                                <p class="header-p">
                                    Jsme dva mladí, šikovní kamarádi s dlouholetými zkušenostmi ve stavebnictví, kteří
                                    našli krásu v řemesle. Díky tomu, že máme bohaté zkušenosti s montáží sádrokartonu,
                                    tak Vám kompletně zhotovíme podhled a v návaznosti na to, bez nutnosti komunikace s
                                    druhou firmou ho i rovnou zateplíme. A jako bonus Vám i můžeme udělat dekorativní
                                    stěrku, kde si jen budete přát.
                                </p>
                            </header>

                            <div class="flex flex-col items-center justify-center">
                                <a
                                    :href="usePage().props.socials.instagram"
                                    rel="noopener noreferrer"
                                    target="_blank"
                                    aria-label="odkaz na Instagram"
                                    class="hover:border-brand-orange hover:bg-brand-orange w-full cursor-pointer rounded-full bg-black/50 px-5 py-5 text-center backdrop-blur-3xl transition-all duration-150 ease-in-out xl:py-4"
                                >
                                    <span class="font-chivo text-center text-white 2xl:text-3xl"> Instagram </span>
                                </a>
                            </div>
                        </div>

                        <div>
                            <img
                                src="/src/imgs/obrazek_03.webp"
                                alt=""
                                class="mt-10 aspect-6/5 w-full max-w-lg rounded-2xl object-cover sm:mt-16 md:max-w-none lg:mt-0"
                            />
                        </div>
                    </div>

                    <div
                        class="mx-auto grid max-w-2xl grid-cols-1 gap-2 overflow-hidden md:mx-0 md:max-w-none md:grid-cols-2 lg:grid-cols-3 xl:gap-8"
                    >
                        <div
                            v-for="(event, index) in timeline"
                            :key="index"
                        >
                            <div
                                class="group mb-2 cursor-default rounded-xl bg-white p-6 shadow-xs shadow-gray-200 transition-all duration-200 ease-linear hover:bg-black/50"
                            >
                                <p
                                    :datetime="event.dateTime"
                                    class="font-chivo text-brand-orange text-md/6 flex items-center text-lg/8 font-semibold tracking-tight group-hover:text-orange-300"
                                >
                                    {{ event.name }}

                                    <span
                                        class="bg-brand-orange absolute -ml-2 block h-px w-screen -translate-x-full group-hover:hidden sm:-ml-4 md:hidden lg:static lg:-mr-6 lg:ml-8 lg:block lg:w-auto lg:flex-auto lg:translate-x-0"
                                        aria-hidden="true"
                                    />
                                </p>

                                <p class="mt-1 text-base/7 text-gray-600 group-hover:text-gray-200">
                                    {{ event.description }}
                                </p>

                                <ul
                                    v-if="event.certificates"
                                    class="mt-4 grid grid-cols-2 gap-4"
                                >
                                    <li
                                        v-for="(certificate, certIndex) in event.certificates"
                                        :key="certIndex"
                                    >
                                        <a
                                            :href="certificate.link"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            :aria-label="'certifikát ' + certificate.name"
                                            class="text-brand-orange inline-flex items-center text-sm font-medium group-hover:text-orange-300 hover:text-orange-200"
                                        >
                                            <DocumentIcon class="mr-1 size-4" />
                                            {{ certificate.name }}
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div class="flex scroll-mt-24 flex-col lg:scroll-mt-16">
                        <div class="grid w-full place-content-center items-center justify-center gap-8 lg:grid-cols-5">
                            <img
                                :src="hoveredService.img.src"
                                :alt="hoveredService.img.alt"
                                class="aspect-4/5 h-90 w-full rounded-2xl bg-gray-50 object-cover md:mx-auto md:size-96 lg:col-span-2 lg:aspect-auto lg:h-auto lg:w-auto"
                            />

                            <h3 class="sr-only">Služby</h3>
                            <ul
                                role="list"
                                v-on:mouseleave="hoveredService = defaultSelectedService"
                                class="ml-auto flex h-full flex-col items-center space-y-2 lg:col-span-3 lg:w-10/12 xl:space-y-4"
                            >
                                <li
                                    v-for="(service, index) in services"
                                    :key="index"
                                    class="w-full"
                                >
                                    <dl
                                        v-on:mouseover="hoveredService = service"
                                        @click="hoveredService = service"
                                        :class="[
                                            hoveredService.id === service.id ? 'bg-black/50' : 'bg-white',
                                            'group relative !w-full cursor-pointer rounded-xl p-6 shadow-xs shadow-gray-200 transition-all duration-200 ease-linear hover:bg-black/50',
                                        ]"
                                    >
                                        <dt class="sr-only">Název</dt>
                                        <dd
                                            :class="
                                                'font-chivo text-xl font-medium text-gray-900 group-hover:text-white ' +
                                                (hoveredService.id === service.id ? 'text-white' : '')
                                            "
                                        >
                                            {{ service.name }}
                                        </dd>
                                        <dt class="sr-only">Popisek</dt>
                                        <dd
                                            :class="
                                                'mt-2 w-full flex-none text-base/7 text-gray-600 group-hover:text-gray-200 ' +
                                                (hoveredService.id === service.id ? 'text-white' : '')
                                            "
                                        >
                                            {{ service.description }}
                                        </dd>
                                    </dl>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div class="lg:my-26">
                        <ContactCTA />
                    </div>
                </div>
            </section>
        </template>
    </AppLayout>
</template>
